import store, { billingAddressActions, useAppDispatch, useAppSelector } from '../data/store';
import { useEffect } from 'react';
import { updateControl } from '../../../../domain/validation/validation';
import { CANADA_ID, getCountriesAndStates, USA_ID } from '../../../../domain/addresses/addresses-provider';
import { saveCartDataToStorage } from '../data/cart-storage-provider';
import {
    IBillingControls,
    ICartData,
    ILocalDataRequest,
    IMaxMindAddress,
    IShippingControls,
    ITaxAddressData
} from '../interfaces';
import { getJSONFromHtml } from '../../../../domain/common';
import { ICountry } from '../../../../domain/addresses/interfaces';
import { TaxIdStatusEnum } from '../enums/tax-id-status-enum';
import { formatCartData } from '../data/data-provider';

/**
 * update address after login (after 'updateCount' property changes)
 */
export const useUpdateAddressFromResponse = () => {
    const dispatch = useAppDispatch();

    const cartData = useAppSelector(store => store.root.cartData);
    const updateCount = useAppSelector(store => store.billingAddress.updateCount);

    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingAddressLine2Control = useAppSelector(store => store.billingAddress.addressLine2Control);
    const cityControl = useAppSelector(store => store.billingAddress.cityControl);
    const zipCodeControl = useAppSelector(store => store.billingAddress.zipCodeControl);
    const countryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const stateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const phoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const phoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    // https://wavesaudio.atlassian.net/browse/DSWEB-6
    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);

    // https://wavesaudio.atlassian.net/browse/DSWEB-3281
    // https://dev.maxmind.com/geoip/docs/web-services/requests
    // https://github.com/maxmind/GeoIP2-dotnet/tree/main
    const maxMindAddress = getMaxMindAddressData(cartData.maxMindAddress, getCountriesAndStates());

    useEffect(() => {
            dispatch(
                billingAddressActions.setControlsList({
                    controls: [
                        {
                            controlName: 'billToControl',
                            control: updateControl(billToControl, cartData.billingAddress?.addressPersonalName || '')
                        },
                        {
                            controlName: 'addressLine1Control',
                            control: updateControl(billingAddressLine1Control, cartData.billingAddress?.addressLine1 || '')
                        },
                        {
                            controlName: 'addressLine2Control',
                            control: updateControl(billingAddressLine2Control, cartData.billingAddress?.addressLine2 || '')
                        },
                        {
                            controlName: 'countryIdControl',
                            control: updateControl(countryIdControl, Number(cartData.billingAddress?.addressCountryID) || maxMindAddress?.countryId || 0)
                        },
                        {
                            controlName: 'stateIdControl',
                            control: updateControl(stateIdControl, Number(cartData.billingAddress?.addressStateID) || maxMindAddress?.stateId || 0)
                        },
                        {
                            controlName: 'cityControl',
                            control: updateControl(cityControl, cartData.billingAddress?.addressCity || '') //  || maxMindAddress?.cityName
                        },
                        {
                            controlName: 'zipCodeControl',
                            control: updateControl(zipCodeControl, cartData.billingAddress?.addressZip || '')
                        },
                        {
                            controlName: 'phoneControl',
                            control: updateControl(phoneControl, cartData.billingAddress?.phoneNumber || '')
                        },
                        {
                            controlName: 'phoneCountryIdControl',
                            control: updateControl(phoneCountryIdControl, Number(cartData.billingAddress?.phoneCountryID) || USA_ID)
                        },
                        {
                            controlName: 'taxIdControl',
                            control: updateControl(taxIdControl, cartData.billingAddress?.taxID || '')
                        },
                        {
                            controlName: 'taxIdSelectedStatusControl',
                            control: updateControl(
                                taxIdSelectedStatusControl,
                                Number(cartData.billingAddress?.taxIDSelectedStatus) || TaxIdStatusEnum.NONE
                            )
                        }
                    ],
                })
            );
        },
        // the dependencies list should not contain controls, otherwise we have an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            dispatch, updateCount,
        ]
    );
};

/**
 * each time address controls change ---> save updated cart data to the storage
 * for example, after user input
 */
export const useSaveAddressesToStorage = () => {

    const cartData = useAppSelector(store => store.root.cartData);

    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingAddressLine2Control = useAppSelector(store => store.billingAddress.addressLine2Control);
    const cityControl = useAppSelector(store => store.billingAddress.cityControl);
    const zipCodeControl = useAppSelector(store => store.billingAddress.zipCodeControl);
    const countryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const stateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const phoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const phoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    // https://wavesaudio.atlassian.net/browse/DSWEB-6
    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);

    const isShippingDifferentFromBilling = useAppSelector(store => store.shippingAddress.isShippingDifferentFromBilling);
    const shipToControl = useAppSelector(store => store.shippingAddress.shipToControl);
    const shipAddressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);
    const shipAddressLine2Control = useAppSelector(store => store.shippingAddress.addressLine2Control);
    const shipCityControl = useAppSelector(store => store.shippingAddress.cityControl);
    const shipCountryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);
    const shipStateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);
    const shipZipCodeControl = useAppSelector(store => store.shippingAddress.zipCodeControl);
    const shipPhoneControl = useAppSelector(store => store.shippingAddress.phoneControl);
    const shipPhoneCountryIdControl = useAppSelector(store => store.shippingAddress.phoneCountryIdControl);

    useEffect(() => {
            const updatedCartData = {...cartData};

            updatedCartData.billingAddress = {
                ...cartData.billingAddress,
                addressPersonalName: billToControl.value,
                addressLine1: billingAddressLine1Control.value,
                addressLine2: billingAddressLine2Control.value,
                addressCountryID: Number(countryIdControl.value) || 0,
                addressStateID: Number(stateIdControl.value) || 0,
                addressCity: cityControl.value,
                addressZip: zipCodeControl.value,
                phoneNumber: phoneControl.value,
                phoneCountryID: Number(phoneCountryIdControl.value) || USA_ID,

                taxID: taxIdControl.value,
                taxIDSelectedStatus: Number(taxIdSelectedStatusControl.value) || TaxIdStatusEnum.NONE,
            };

            if(isShippingDifferentFromBilling) {
                updatedCartData.shippingAddress = {
                    ...cartData.shippingAddress,
                    addressPersonalName: shipToControl.value,
                    addressLine1: shipAddressLine1Control.value,
                    addressLine2: shipAddressLine2Control.value,
                    addressCountryID: Number(shipCountryIdControl.value) || 0,
                    addressStateID: Number(shipStateIdControl.value) || 0,
                    addressCity: shipCityControl.value,
                    addressZip: shipZipCodeControl.value,
                    phoneNumber: shipPhoneControl.value,
                    phoneCountryID: Number(shipPhoneCountryIdControl.value) || USA_ID,
                };
            }
            else{
                updatedCartData.shippingAddress = undefined;
            }

            saveCartDataToStorage(updatedCartData);
        },
        // the dependencies list should not contain cartData
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            billToControl, billingAddressLine1Control, billingAddressLine2Control,
            cityControl, zipCodeControl, countryIdControl, stateIdControl,
            phoneControl, phoneCountryIdControl, taxIdControl, taxIdSelectedStatusControl,

            isShippingDifferentFromBilling,

            shipToControl, shipAddressLine1Control, shipAddressLine2Control,
            shipCityControl, shipZipCodeControl, shipCountryIdControl, shipStateIdControl,
            shipPhoneControl, shipPhoneCountryIdControl,
        ]);
};

/**
 * fore security reasons, we don't store address IDs in the session storage
 */
export const getAddressIDs = () => {
    const initialCartData: ICartData = formatCartData(getJSONFromHtml(document.getElementById('preloaded-data')));

    return {
        billingAddressID: Number(initialCartData.billingAddress?.addressID) || 0,
        shippingAddressID: Number(initialCartData.shippingAddress?.addressID) || 0,
    }
};

/**
 * Check if summary should be visible
 * depending on the billing & shipping address;
 * Return false when the 'edit form' should be visible.
 * Return true, when the summary should be visible.
 */
export const defineIfSummaryVisible = (
    countries: ICountry[],
    billingControls: IBillingControls,
    cartData: ICartData,
    isShippingDifferentFromBilling: boolean,
    shippingControls: IShippingControls,
) => {

    const isNotEmpty = (text: string) => {
        if(!text) return false;
        return text.trim() !== '';
    };

    let isVisible = isNotEmpty(billingControls.billToControl.value) &&
        isNotEmpty(billingControls.addressLine1Control.value) &&
        isNotEmpty(billingControls.cityControl.value) &&
        isNotEmpty(billingControls.phoneControl.value);
    if(!isVisible) return false;

    // country should be selected
    const countryID = Number(billingControls.countryIdControl.value) || 0;
    if(countryID === 0) return false;

    // country should present in the country list
    const selectedCountry = countries.find(country => country.countryID.toString() === countryID.toString());
    if(!selectedCountry) return false;

    // phone country should be selected
    const phoneCountryId = Number(billingControls.phoneCountryIdControl.value) || 0;
    if(phoneCountryId === 0) return false;

    // if country has states ---> state should be selected
    if(selectedCountry.states && selectedCountry.states.length > 0){
        const stateID = Number(billingControls.stateIdControl.value) || 0;
        if(stateID === 0) return false;

        const found = selectedCountry.states.find(state => state.stateID.toString() === stateID.toString());
        if(!found) return false;
    }

    // validate tax id
    if(selectedCountry.isTaxID){

        if(billingControls.taxIdSelectedStatusControl.value === TaxIdStatusEnum.NONE){
            return false;
        }

        if(billingControls.taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES){

            // tax id control should not be empty
            if(!isNotEmpty(billingControls.taxIdControl.value)){
                return false;
            }

            const taxId = billingControls.taxIdControl.value;
            const taxIDMinLength = Number(selectedCountry.taxIDMinLength) || 0;
            const taxIDMaxLength = Number(selectedCountry.taxIDMaxLength) || 0;

            // validate tax id length
            if( (taxIDMinLength > 0 && taxId.length < taxIDMinLength) ||
                (taxIDMaxLength > 0 && taxId.length > taxIDMaxLength)){
                return false;
            }
        }
    }

    // digigrid products can have usa / canada countries only
    const hasDigigridItem = cartData.cartItems.find(cartItem => cartItem.skuShippingUSCanada && cartItem.isActive);
    if(hasDigigridItem && selectedCountry.countryID !== USA_ID && selectedCountry.countryID !== CANADA_ID){
        return false;
    }

    if(isShippingDifferentFromBilling) {
        let isVisible = isNotEmpty(shippingControls.shipToControl.value) &&
            isNotEmpty(shippingControls.addressLine1Control.value) &&
            isNotEmpty(shippingControls.cityControl.value) &&
            isNotEmpty(shippingControls.phoneControl.value);
        if(!isVisible) return false;

        // country should be selected
        const countryID = Number(shippingControls.countryIdControl.value) || 0;
        if(countryID === 0) return false;

        // country should present in the country list
        const selectedCountry = countries.find(country => country.countryID.toString() === countryID.toString());
        if(!selectedCountry) return false;

        // phone country should be selected
        const phoneCountryId = Number(shippingControls.phoneCountryIdControl.value) || 0;
        if(phoneCountryId === 0) return false;

        // if country has states ---> state should be selected
        if(selectedCountry.states && selectedCountry.states.length > 0){
            const stateID = Number(shippingControls.stateIdControl.value) || 0;
            if(stateID === 0) return false;

            const found = selectedCountry.states.find(state => state.stateID.toString() === stateID.toString());
            if(!found) return false;
        }
    }

    return isVisible;
};

/**
 * Almost every 'get cart data' request should provide
 * updated address and other data that is not stored in session or in database.
 * This data is used to retrieve tax and shipping prices.
 */
export const getLocalData = () : ILocalDataRequest => {
    const state = store.getState();

    const countries = state.root.countries;

    const billingCountryIdControl = state.billingAddress.countryIdControl;
    const shippingCountryIdControl = state.shippingAddress.countryIdControl;

    const billingStateIdControl = state.billingAddress.stateIdControl;
    const shippingStateIdControl = state.shippingAddress.stateIdControl;

    const billingZipCodeControl = state.billingAddress.zipCodeControl;
    const shippingZipCodeControl = state.shippingAddress.zipCodeControl;

    const billingSelectedCountry = countries.find(country => country.countryID.toString() === billingCountryIdControl.value.toString());
    const shippingSelectedCountry = countries.find(country => country.countryID.toString() === shippingCountryIdControl.value.toString());

    const billingSelectedState = billingSelectedCountry && billingSelectedCountry.states.find(state => state.stateID.toString() === billingStateIdControl.value.toString());
    const shippingSelectedState = shippingSelectedCountry && shippingSelectedCountry.states.find(state => state.stateID.toString() === shippingStateIdControl.value.toString());

    const isShippingDifferentFromBilling = state.shippingAddress.isShippingDifferentFromBilling;

    const billingAddressLine1Control = state.billingAddress.addressLine1Control;
    const shippingAddressLine1Control = state.shippingAddress.addressLine1Control;

    const billingCityControl = state.billingAddress.cityControl;
    const shippingCityControl = state.shippingAddress.cityControl;

    const shippingCode = state.shippingAddress.upsShippingCodeControl.value;

    const taxIdControl = state.billingAddress.taxIdControl;
    const taxIdSelectedStatusControl = state.billingAddress.taxIdSelectedStatusControl;
    const taxID = taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES ? taxIdControl.value : '';

    return {
        // tax --------------------
        taxCountry2LetterCode: isShippingDifferentFromBilling ? (shippingSelectedCountry?.country2Digit || '') : billingSelectedCountry?.country2Digit || '',
        taxState2LetterCode: isShippingDifferentFromBilling ? (shippingSelectedState?.state2Digit || '') : billingSelectedState?.state2Digit || '',
        taxZipCode: isShippingDifferentFromBilling ? shippingZipCodeControl.value : billingZipCodeControl.value,

        shipAddressLine: isShippingDifferentFromBilling ? shippingAddressLine1Control.value : billingAddressLine1Control.value,
        shipCity: isShippingDifferentFromBilling ? shippingCityControl.value : billingCityControl.value,
        shipCountry2LetterCode: isShippingDifferentFromBilling ? shippingSelectedCountry?.country2Digit : billingSelectedCountry?.country2Digit,
        shipState2LetterCode: isShippingDifferentFromBilling ? shippingSelectedState?.state2Digit : billingSelectedState?.state2Digit,
        shipZipCode: isShippingDifferentFromBilling ? shippingZipCodeControl.value : billingZipCodeControl.value,
        shippingCode,

        step: state.root.cartStep,
        taxID,
    } as ILocalDataRequest;
};

/**
 * this country / state data is used for tax calculations
 */
export const getTaxAddressData = (
    countries: ICountry[],
    countryId: number,
    stateId: number,
    zipCode: string
) : ITaxAddressData|null => {
    const selectedCountry = countries.find(country => country.countryID.toString() === countryId.toString());
    if(!selectedCountry) return null;

    const selectedState = selectedCountry && selectedCountry.states.find(state => state.stateID.toString() === stateId.toString());

    return {
        taxCountry2LetterCode: selectedCountry.country2Digit,
        taxState2LetterCode: selectedState?.state2Digit || '',
        taxZipCode: zipCode,
    };
};

/**
 * If the cart contains at least one digigrid product, the countries
 * list should contain only USA and Canada. Otherwise, it should contain
 * the whole countries list.
 */
export const getCartCountries = (countries: ICountry[], cartData: ICartData) => {
    if(!cartData || !cartData.cartItems || cartData.cartItems.length <= 0) return countries;

    const hasDigigridItem = cartData.cartItems.find(cartItem => cartItem.skuShippingUSCanada && cartItem.isActive);
    if(!hasDigigridItem) return countries;

    // filter countries
    return countries.filter(country => (country.countryID === USA_ID || country.countryID === CANADA_ID));
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-3281
 */
export const getMaxMindAddressData = (cartMaxMind: IMaxMindAddress, countries: ICountry[]) => {
    if(!cartMaxMind) return null;

    const data = {
        cityName: '', // cartMaxMind.cityName || '',
        countryId: 0,
        stateId: 0,
    };

    const countryCode = cartMaxMind.countryCode || '';
    if(!countryCode || !countries || countries.length <= 0) return data;

    const country = countries.find(_country => _country.country2Digit.toLowerCase() === countryCode.toLowerCase());
    if(!country) return data;
    data.countryId = country.countryID;

    if(country.states && country.states.length > 0) {
        const stateCode = cartMaxMind.stateCode || '';
        const state = country.states.find(_state => _state.state2Digit.toLowerCase() === stateCode.toLowerCase());

        if(state) {
            data.stateId = state.stateID;
        }
    }

    return data;
};
